import { useEffect, useState, useCallback } from 'react';
import {
  KeyRound,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  Loader2,
  Eye,
  EyeOff,
  RefreshCw,
} from 'lucide-react';
import { useApi } from '../contexts/ApiContext';
import { ApiResponse } from '../types';
import PageHeader from '../components/ui/PageHeader';
import EmptyState from '../components/ui/EmptyState';
import { SkeletonCard } from '../components/ui/Skeleton';

interface PendingAccount {
  id: string;
  registrar_type: string;
  registrar_name: string;
  label: string;
  username_hint: string | null;
  onepassword_vault_id: string;
  onepassword_item_id: string;
  required_fields: string[];
  api_key_url: string;
  setup_note: string | null;
}

interface SetupResult {
  status: 'success' | 'error';
  message: string;
}

const SECRET_FIELDS = new Set(['api_key', 'api_secret', 'client_secret', 'password']);

const FIELD_LABELS: Record<string, string> = {
  api_key: 'API Key',
  api_secret: 'API Secret',
  client_id: 'Client ID',
  client_secret: 'Client Secret',
  client_ip: 'Whitelisted IP',
  username: 'Username',
  password: 'Password',
};

export default function ApiSetupPage() {
  const { fetchApi } = useApi();
  const [pending, setPending] = useState<PendingAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [values, setValues] = useState<Record<string, Record<string, string>>>({});
  const [reveal, setReveal] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, SetupResult>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchApi<ApiResponse<PendingAccount[]>>('/accounts/pending-api-setup');
      if (res.data) setPending(res.data);
    } catch (error) {
      console.error('Failed to load pending accounts:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchApi]);

  useEffect(() => {
    load();
  }, [load]);

  const updateValue = (accountId: string, field: string, value: string) => {
    setValues(prev => ({
      ...prev,
      [accountId]: { ...(prev[accountId] || {}), [field]: value },
    }));
  };

  const toggleReveal = (key: string) => {
    setReveal(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const submit = async (account: PendingAccount) => {
    const credentials = values[account.id] || {};
    const missing = account.required_fields.filter(f => !credentials[f]?.trim());
    if (missing.length > 0) {
      setResults(prev => ({
        ...prev,
        [account.id]: {
          status: 'error',
          message: `Missing: ${missing.map(m => FIELD_LABELS[m] || m).join(', ')}`,
        },
      }));
      return;
    }

    setSubmitting(account.id);
    setResults(prev => {
      const next = { ...prev };
      delete next[account.id];
      return next;
    });

    try {
      const res = await fetchApi<ApiResponse<{ written_fields: string[]; validation_message: string }>>(
        `/accounts/${account.id}/setup-credentials`,
        {
          method: 'POST',
          body: JSON.stringify({ credentials }),
        }
      );
      if (res.success) {
        setResults(prev => ({
          ...prev,
          [account.id]: {
            status: 'success',
            message: `Validated and written to 1Password (${res.data?.written_fields.join(', ')}).`,
          },
        }));
        // Remove from list after a short pause so the user sees the success state
        setTimeout(() => {
          setPending(prev => prev.filter(p => p.id !== account.id));
          setResults(prev => {
            const next = { ...prev };
            delete next[account.id];
            return next;
          });
          setValues(prev => {
            const next = { ...prev };
            delete next[account.id];
            return next;
          });
        }, 2500);
      } else {
        setResults(prev => ({
          ...prev,
          [account.id]: { status: 'error', message: res.error || 'Unknown error' },
        }));
      }
    } catch (error) {
      setResults(prev => ({
        ...prev,
        [account.id]: {
          status: 'error',
          message: error instanceof Error ? error.message : 'Request failed',
        },
      }));
    } finally {
      setSubmitting(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="API Credential Setup" />
        <SkeletonCard lines={5} />
        <SkeletonCard lines={5} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <KeyRound className="h-6 w-6 text-brand-600" />
            API Credential Setup
          </span>
        }
        description={
          <span className="block max-w-2xl">
            For each 1Password-linked account below, open the registrar's API key page,
            paste the credentials here, and click <span className="font-medium">Validate &amp; Save</span>.
            Credentials are tested against the live API before being written back to the 1Password item.
          </span>
        }
        actions={
          <button onClick={load} className="btn btn-secondary shrink-0">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
        }
      />

      {pending.length === 0 ? (
        <div className="bg-surface border border-slate-200 rounded-lg">
          <EmptyState
            icon={CheckCircle}
            title="All 1Password-linked accounts are API ready."
            description="New accounts created via 1Password integration will appear here when they need credentials."
          />
        </div>
      ) : (
        <div className="space-y-4">
          {pending.map(account => {
            const result = results[account.id];
            const isSubmitting = submitting === account.id;
            const accountValues = values[account.id] || {};

            return (
              <div
                key={account.id}
                className="bg-surface border border-slate-200 rounded-lg overflow-hidden"
              >
                <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-slate-900">{account.registrar_name}</h3>
                      <span className="text-sm text-slate-500">·</span>
                      <span className="text-sm text-slate-600">{account.label}</span>
                    </div>
                    {account.username_hint && (
                      <p className="text-xs text-slate-500 mt-0.5 font-mono">{account.username_hint}</p>
                    )}
                  </div>
                  {account.api_key_url ? (
                    <a
                      href={account.api_key_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-secondary"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Open API key page
                    </a>
                  ) : (
                    <span className="text-xs text-slate-400 italic">No self-serve URL</span>
                  )}
                </div>

                {account.setup_note && (
                  <div className="px-5 py-3 bg-amber-50 border-b border-amber-100 text-sm text-amber-800 flex gap-2">
                    <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>{account.setup_note}</span>
                  </div>
                )}

                <div className="p-5 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {account.required_fields.map(field => {
                      const isSecret = SECRET_FIELDS.has(field);
                      const revealKey = `${account.id}:${field}`;
                      const revealed = !!reveal[revealKey];
                      return (
                        <div key={field}>
                          <label className="block text-xs font-medium text-slate-600 mb-1">
                            {FIELD_LABELS[field] || field}
                            <span className="text-slate-400 font-normal font-mono ml-1.5">
                              ({field})
                            </span>
                          </label>
                          <div className="relative">
                            <input
                              type={isSecret && !revealed ? 'password' : 'text'}
                              value={accountValues[field] || ''}
                              onChange={e => updateValue(account.id, field, e.target.value)}
                              autoComplete="off"
                              spellCheck={false}
                              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md font-mono focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 pr-9"
                              placeholder={`Paste ${field}…`}
                              disabled={isSubmitting}
                            />
                            {isSecret && (
                              <button
                                type="button"
                                onClick={() => toggleReveal(revealKey)}
                                className="absolute inset-y-0 right-0 px-2 text-slate-400 hover:text-slate-600"
                                tabIndex={-1}
                              >
                                {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {result && (
                    <div
                      className={`flex gap-2 p-3 rounded-md text-sm ${
                        result.status === 'success'
                          ? 'bg-emerald-50 text-emerald-800'
                          : 'bg-red-50 text-red-800'
                      }`}
                    >
                      {result.status === 'success' ? (
                        <CheckCircle className="h-4 w-4 shrink-0 mt-0.5" />
                      ) : (
                        <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                      )}
                      <span>{result.message}</span>
                    </div>
                  )}

                  <div className="flex justify-end">
                    <button
                      onClick={() => submit(account)}
                      disabled={isSubmitting}
                      className="btn btn-primary"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Validating…
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Validate &amp; Save to 1Password
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
